import React, { memo, useEffect, useMemo, useState } from "react";
import {
  BoltOutlined,
  LaunchOutlined,
  LinkOutlined,
  ListOutlined,
  Home,
  Close,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";

import ElementHeaderJs from "../elementHeader";
import CardPotenciaJs from "./cardPotencia";
import VirtualizedAlertsJs from "../sideBarEasyMapsRp/cardAlerts/virtualizacao";
import VirtualizedONUJs from "../sideBarEasyMapsOnus/cardONU/vitualizacao";
import { useMapa } from "../../../contexts/mapaContext";

const ElementHeader = ElementHeaderJs as React.ComponentType<any>;
const CardPotencia = CardPotenciaJs as React.ComponentType<any> & {
  AccordionSummaryFtth: React.ComponentType<any>;
  MaisDetalhesFtth: React.ComponentType<any>;
};
const VirtualizedAlerts = VirtualizedAlertsJs as React.ComponentType<any>;
const VirtualizedONU = VirtualizedONUJs as React.ComponentType<any>;

interface HostConfig {
  host?: string;
  hostid?: string | number;
  interfaces?: unknown[];
  tags?: unknown[];
}

interface ElementData {
  snmp?: any[];
  latencia?: any[];
  ping?: any[];
  perdaPing?: any[];
  itens?: any[];
}

interface ElementConfig {
  config?: {
    hosts?: HostConfig[];
  };
}

interface EasyMapsElement {
  id?: string | number;
  titulo?: string;
  alerts?: any[];
  elementConfig?: ElementConfig;
  img?: string | number;
  tipo?: number;
  mapRef?: any;
  mapRefId?: string | number;
  color?: string;
  onus?: (string | number)[];
  tipoElemento?: "cto" | "onu";
  tipoBgpIxc?: unknown;
  hasCto?: boolean;
  data?: ElementData;
}

interface RightBarEasyMapsOnusProps {
  element: EasyMapsElement | null;
  mapId?: string | number | null;
  iconsElement?: any[];
  setElement?: (element: EasyMapsElement | null) => void;
  fullScreem?: boolean;
  link?: string;
  setOpen?: (value: boolean) => void;
  setFocusNodeId?: (id: string | number | null) => void;
  focusZoom?: (...args: any[]) => void;
  setOpenConnection?: (value: any) => void;
  elementONUSelected?: any;
  setElementONUSelected?: (value: any) => void;
  openConnection?: any;
  isOnuExpanded?: boolean;
}

interface OutletContextType {
  config: any;
  configLayer: any;
}

const RightBarEasyMapsOnus = memo<RightBarEasyMapsOnusProps>(
  ({
    element,
    mapId = null,
    iconsElement = [],
    setElement = () => {},
    fullScreem = false,
    link = "",
    setOpen = () => {},
    setFocusNodeId = () => {},
    focusZoom = () => {},
    setOpenConnection = () => {},
    elementONUSelected,
    setElementONUSelected = () => {},
  }) => {
    const {
      id,
      titulo,
      elementConfig,
      img,
      tipo,
      mapRef,
      mapRefId,
    } = element || {};

    const [enlacesConectados, setEnlacesConectados] = useState<any[]>([]);
    const [valueTab, setValueTab] = useState<number>(1);
    const [expanded, setExpanded] = useState<any>(null);
    const [hosts, setHosts] = useState<any[]>([]);

    const navigate = useNavigate();
    const theme = useTheme();

    const { config, configLayer } = useOutletContext<OutletContextType>();

    const {
      connections,
      onusMap,
      iconsElements,
      elementSelected,
    } = useMapa() as any;

    const elementsONUs = useMemo(() => {
      const onusSet = new Set(element?.onus ?? []);
      return Array.from(onusMap.values()).filter((onu: any) =>
        onusSet.has(onu.id),
      );
    }, [onusMap, element]);

    const styles = {
      line: {
        backgroundColor: element?.color,
        height: "60%",
        width: "3px",
        ml: "7px",
        mr: "-5px",
      },
      card: {
        borderRadius: "5px",
        backgroundColor: theme.palette.background.paperSelectedMaps,
        boxShadow: "0px 0px 3px  #0000001a",
        border: "none",
        "&::before": { display: "none" },
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "start",
      },
      drawer: {
        position: "absolute",
        clipPath: "none",
        marginTop: "65px",
        flexShrink: 0,
        overflow: "visible",
        "& .MuiDrawer-paper": {
          borderRadius: "7px",
          color: theme.palette.text.primary,
          mr: "10px",
          height: fullScreem
            ? { xs: "calc(100vh - 50px)", sm: "calc(100vh - 20px)" }
            : { xs: "calc(100vh - 120px)", sm: "calc(100vh - 85px)" },
          width: "350px",
          padding: "0px 15px",
          marginTop: fullScreem ? "10px" : "70px",
          overflow: "visible",
          backgroundColor: theme.palette.background.sideBar,
          border: "1px solid " + theme.palette.color.appBarBorder,
        },
      },
      contentTab: {
        mt: 2,
        gap: 1,
        maxHeight: fullScreem ? "68vh" : "60.5vh",
        overflow: "auto",
        boxSizing: "content-box",
      },
      text: {
        fontSize: "0.875rem",
        color: theme.palette.color.text,
      },
      data: {
        overflow: "auto",
        height: "100%",
        paddingBottom: "15px",
      },
      tabs: {
        width: "100%",
        minHeight: "20px",
        "& button": {
          color: theme.palette.text.disabled,
          textTransform: "none",
          minHeight: "20px",
          Height: "100%",
          borderRadius: "5px",
          paddingX: "10px",
        },
        "& .MuiTab-icon": { marginRight: "2px" },
        "& button.Mui-selected": {
          backgroundColor: theme.palette.background.tab,
        },
        "& .MuiTabs-indicator": {
          backgroundColor: "transparent",
          border: "none",
        },
      },
      buttonReturn: {
        mt: 2,
        backgroundColor: theme.palette.background.paperSelectedMaps,
        color: theme.palette.text.disabled,
        textTransform: "none",
        boxShadow: "0px 0px 3px #0000001a",
        "&:hover": {
          backgroundColor: theme.palette.background.hover,
          boxShadow: "0px 0px 3px #0000001a",
          color: theme.palette.primary.main,
        },
      },
    };

    const handleChangeTab = (
      _: React.SyntheticEvent,
      newValue: number,
    ) => {
      setValueTab(newValue);
    };

    useEffect(() => {
      if (!element || !connections) return;

      const enlaces = [...connections.values()].filter(
        ({ referenceId, destinationId }: any) =>
          id === destinationId || id === referenceId,
      );

      setEnlacesConectados(enlaces);
    }, [connections, element, id]);

    useEffect(() => {
      setValueTab(1);
    }, [element?.id]);

    useEffect(() => {
      if (!element?.id) return;

      const hostsProcess: any[] = [];
      let hostsElement: HostConfig[] = [];

      if (tipo === 0) {
        hostsElement = elementConfig?.config?.hosts ?? [];
      } else {
        hostsElement =
          mapRef?.Element?.flatMap(
            ({ elementConfig }: any) =>
              elementConfig?.config?.hosts ?? [],
          ) ?? [];
      }

      hostsElement.forEach(({ host, hostid }) => {
        const snmp = element?.data?.snmp?.find(
          (item: any) => item.hostid == hostid,
        )?.snmp_available;
        const latencia = element?.data?.latencia?.find(
          (item: any) => item.hostid == hostid,
        )?.lastvalue;
        const ping = element?.data?.ping?.find(
          (item: any) => item.hostid == hostid,
        )?.lastvalue;
        const perdaPing = element?.data?.perdaPing?.find(
          (item: any) => item.hostid == hostid,
        )?.lastvalue;
        const itens = element?.data?.itens?.filter(
          (item: any) => item.hostid == hostid,
        );

        hostsProcess.push({ name: host, hostid, perdaPing, ping, snmp, latencia, itens });
      });

      setHosts(hostsProcess);
    }, [element, tipo, elementConfig, mapRef]);

    const onClose = () => {
      setOpen(false);
      setExpanded(null);
      setOpenConnection(null);
      setFocusNodeId(null);
    };

    useEffect(() => {
      if (element === null) {
        onClose();
      }
    }, [element]);

    const handleMouseDown = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      const url = `${window.location.origin}${link}`;
      if (event.button === 1) {
        window.open(url, "_blank");
      }
    };

    return (
      <>
        <Drawer
          open={!!element?.id}
          onAbort={onClose}
          anchor="right"
          sx={styles.drawer}
          variant="persistent"
        >
          <Stack
            sx={{ paddingY: "12px" }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
              {"Detalhes da " +
                (element?.tipoElemento === "cto" ? "CTO" : "ONU")}
            </Typography>
            <Stack gap={1} direction="row">
              {tipo != 0 && (
                <Tooltip title="ir para o mapa">
                  <IconButton
                    disabled={!mapRefId}
                    color="primary"
                    onMouseDown={handleMouseDown}
                    onClick={() => {
                      onClose();
                      setElement(null);
                      navigate(link);
                    }}
                  >
                    <LaunchOutlined sx={{ fontSize: "20px" }} />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => setElement(null)}>
                <Close
                  sx={{ fontSize: "20px", color: theme.palette.text.disabled }}
                />
              </IconButton>
            </Stack>
          </Stack>

          <Stack sx={styles.card}>
            <Box sx={styles.line} />
            <ElementHeader
              mapa={mapRef}
              tipo={tipo}
              titulo={titulo}
              interfaces={
                Array.isArray(elementConfig?.config?.hosts) &&
                (elementConfig?.config?.hosts?.length ?? 0) > 0
                  ? elementConfig?.config?.hosts?.[0]?.interfaces
                  : []
              }
              tags={
                Array.isArray(elementConfig?.config?.hosts) &&
                (elementConfig?.config?.hosts?.length ?? 0) > 0
                  ? elementConfig?.config?.hosts?.[0]?.tags
                  : []
              }
              img={
                iconsElement?.find((icon: any) => icon.id == img)?.img ||
                iconsElement?.find(
                  (icon: any) =>
                    icon.nome ==
                    (element?.tipo === 0
                      ? "_roteador"
                      : element?.tipo === 1
                        ? "_mapa"
                        : "_pop"),
                )?.img
              }
            />
          </Stack>

          {element?.tipoBgpIxc != null && (
            <>
              {element?.tipoElemento === "onu" &&
                element?.hasCto !== false && (
                  <Button
                    startIcon={<LinkOutlined />}
                    sx={styles.buttonReturn}
                    variant="contained"
                    fullWidth
                    onClick={() => setElementONUSelected(null)}
                  >
                    Ir para a CTO
                  </Button>
                )}

              <Stack sx={[styles.card, { p: 0.5, mt: 2, height: "50px" }]}>
                <Tabs
                  variant="fullWidth"
                  value={valueTab}
                  onChange={handleChangeTab}
                  textColor="primary"
                  indicatorColor="primary"
                  sx={styles.tabs}
                >
                  <Tab
                    icon={<ListOutlined />}
                    label="Detalhes"
                    value={1}
                    sx={styles.text}
                    iconPosition="start"
                  />
                  <Tab
                    icon={<BoltOutlined />}
                    label="Alertas"
                    value={3}
                    sx={styles.text}
                    iconPosition="start"
                  />
                  {element.tipoElemento === "cto" && (
                    <Tab
                      icon={<Home />}
                      label="ONUs"
                      value={2}
                      sx={styles.text}
                      iconPosition="start"
                    />
                  )}
                </Tabs>
              </Stack>

              {valueTab === 1 && (
                <Stack sx={styles.contentTab}>
                  <CardPotencia
                    mapId={mapId}
                    tipoEquipamento={
                      element.tipoElemento === "cto" ? "CTO" : "ONU"
                    }
                    conexao={
                      element.tipoElemento === "onu" && elementONUSelected
                        ? elementONUSelected
                        : element
                    }
                    unique={true}
                    element={element}
                    key={"cardOlt" + element.id}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    index={0}
                    layer="ftth"
                  >
                    <CardPotencia.AccordionSummaryFtth />
                    <CardPotencia.MaisDetalhesFtth />
                  </CardPotencia>
                </Stack>
              )}

              {valueTab === 3 && (
                <Stack sx={styles.contentTab}>
                  <VirtualizedAlerts
                    alertasTotais={
                      element.tipoElemento === "onu"
                        ? (element.alerts ?? [])
                        : [
                            ...(element?.alerts ?? []),
                            ...elementsONUs.flatMap((a: any) => a.alerts ?? []),
                          ]
                    }
                    search={focusZoom}
                    style={styles.data}
                  />
                </Stack>
              )}

              {valueTab === 2 && (
                <Stack sx={styles.contentTab}>
                  <VirtualizedONU
                    lista={elementsONUs}
                    style={styles.data}
                    search={(data: any) => {
                      const onu = elementsONUs.find(
                        (onu: any) => onu.id === data,
                      );
                      setElementONUSelected(onu);
                    }}
                    iconsElements={iconsElements}
                  />
                </Stack>
              )}
            </>
          )}
        </Drawer>
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.openConnection === nextProps.openConnection &&
    prevProps.fullScreem === nextProps.fullScreem &&
    JSON.stringify(prevProps.element) ===
      JSON.stringify(nextProps.element) &&
    JSON.stringify(prevProps.elementONUSelected) ===
      JSON.stringify(nextProps.elementONUSelected) &&
    prevProps.isOnuExpanded === nextProps.isOnuExpanded,
);

RightBarEasyMapsOnus.displayName = "RightBarEasyMapsOnus";

export default RightBarEasyMapsOnus;
